import {
  Avatar,
  Badge,
  Button,
  Drawer,
  DrawerProps,
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Select,
  message,
  Space,
} from 'antd'
import { useAttemptListener } from 'dev-masters-react-kit'
import { forwardRef, Ref, useImperativeHandle, useState } from 'react'
import { HiMiniNoSymbol } from 'react-icons/hi2'
import { attemptUpdateSong, EditSongDTO, Song } from '../../../utils/redux/songsSlice'
import { listenerMiddleware, useAppDispatch } from '../../../utils/redux/store'

interface Props extends DrawerProps {
  subTitle?: string
  // bodyStyle?: DrawerStyles['body']
}

export interface EditSongDrawerMethods {
  open: (song: Song) => void
  close: () => void
}

const EditSongDrawer = forwardRef(
  (
    { title, subTitle, height, ...drawerProps }: Props,
    ref: Ref<EditSongDrawerMethods>,
  ) => {
    useImperativeHandle(
      ref,
      (): EditSongDrawerMethods => ({
        open: (song) => {
          setSong(song)
          editSongForm.setFieldsValue(song)
        },
        close: () => setSong(undefined),
      }),
    )

    const [song, setSong] = useState<Song>()
    const [editSongForm] = Form.useForm<EditSongDTO>()

    const isSongEditing = useAttemptListener({
      listenerMiddleware: listenerMiddleware,

      attempt: attemptUpdateSong,
      onFulfilled: () => {
        message.success('Successfully updated!')
        setSong(undefined)
      },
    })

    return (
      <Drawer
        placement="right"
        title="Edit song"
        height={height ?? 'auto'}
        closable={false}
        onClose={() => setSong(undefined)}
        extra={
          <Space>
            <Button onClick={() => setSong(undefined)}>Cancel</Button>
            <Button
              onClick={() => editSongForm.submit()}
              type="primary"
              loading={isSongEditing}
            >
              Submit
            </Button>
          </Space>
        }
        open={!!song}
        {...drawerProps}
      >
        {song && <EditSong editSongForm={editSongForm} song={song} />}
      </Drawer>
    )
  },
)

interface EditSongProps {
  editSongForm: FormInstance<EditSongDTO>
  song: Song
}
function EditSong({ editSongForm, song }: EditSongProps) {
  const dispatch = useAppDispatch()

  function onFinish(values: EditSongDTO) {
    dispatch(
      attemptUpdateSong({
        ...values,
        _id: song._id,
        file: song.file,
      }),
    )
  }

  return (
    <>
      <Flex justify="center" align="center" style={{ marginBottom: '3em' }}>
        <Badge
          // count={
          //   <Button shape="circle">
          //     <ImgCrop rotationSlider showGrid cropShape="round">
          //       <Upload
          //         accept="image/jpeg, image/png, image/jpg"
          //         showUploadList={false}
          //         multiple={false}
          //       >
          //         <FaPencil />
          //       </Upload>
          //     </ImgCrop>
          //   </Button>
          // }
          offset={[0, 150]}
        >
          <Avatar
            size={150}
            shape="square"
            icon={<HiMiniNoSymbol />}
            src={song.imageUrl}
          />
        </Badge>
      </Flex>
      <Form form={editSongForm} onFinish={onFinish} layout="vertical">
        <Form.Item name="title" required label="Title">
          <Input placeholder="Title" />
        </Form.Item>
        <Form.Item name="artist" required label="Artist">
          <Input placeholder="Artist" />
        </Form.Item>

        {/* new fields */}
        <Form.Item name="lang" label="Language">
          <Input placeholder="eng / ua / fr" />
        </Form.Item>

        <Form.Item name="tonality" label="Tonality">
          <Select
            placeholder="Select tonality"
            options={[
              { value: '1A', label: '1A — Темний-загадковий' },
              { value: '1B', label: '1B — Яскравий-піднесений' },
              { value: '2A', label: '2A — Тривожний-емоційний' },
              { value: '2B', label: '2B — Спокійний-чистий' },
              { value: '3A', label: '3A — Сумний-меланхолійний' },
              { value: '3B', label: '3B — Гармонійний-виважений' },
              { value: '4A', label: '4A — Темний-емоційний' },
              { value: '4B', label: '4B — Романтичний-м’який' },
              { value: '5A', label: '5A — Серйозний-глибокий' },
              { value: '5B', label: '5B — Натхненний-теплий' },
              { value: '6A', label: '6A — Драматичний-агресивний' },
              { value: '6B', label: '6B — Енергійний-веселий' },
              { value: '7A', label: '7A — Сумний-задумливий' },
              { value: '7B', label: '7B — Мелодійний-ніжний' },
              { value: '8A', label: '8A — Меланхолійний-медитативний' },
              { value: '8B', label: '8B — Чистий-світлий' },
              { value: '9A', label: '9A — Емоційний-глибокий' },
              { value: '9B', label: '9B — Надійний-комфортний' },
              { value: '10A', label: '10A — Напружений-інтенсивний' },
              { value: '10B', label: '10B — Тріумфальний-героїчний' },
              { value: '11A', label: '11A — Агресивний-хаотичний' },
              { value: '11B', label: '11B — Переможний-життєстверджуючий' },
              { value: '12A', label: '12A — Темний-експериментальний' },
              { value: '12B', label: '12B — Натхненний-блискучий' },
            ]}
          />
        </Form.Item>

        <Form.Item name="bpm" label="BPM">
          <InputNumber min={1} max={999} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="energy" label="Energy">
          <Select
            placeholder="Select energy"
            options={[
              { value: 1, label: '1 — Повна тиша' },
              { value: 2, label: '2 — Дрімота' },
              { value: 3, label: '3 — Легка туга' },
              { value: 4, label: '4 — Розслаблення' },
              { value: 5, label: '5 — Рівновага' },
              { value: 6, label: '6 — Рух вперед' },
              { value: 7, label: '7 — Танцювальна тяга' },
              { value: 8, label: '8 — Потужність' },
              { value: 9, label: '9 — Пік динаміки' },
              { value: 10, label: '10 — Максимум енергії' },
            ]}
          />
        </Form.Item>

        <Form.Item name="tags" label="Tags">
          <Select mode="tags" tokenSeparators={[',']} placeholder="Add tags" />
        </Form.Item>
      </Form>
    </>
  )
}

EditSongDrawer.displayName = 'EditSongDrawer'
export default EditSongDrawer
